const ALLOWED_CONTENT_TYPES = [
  'image/*', // For IDs, licenses, and scanned documents
  'application/pdf', // Most common format for official documents
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/msword', // .doc
  'application/vnd.ms-excel', // .xls
  'text/csv', // For financial data
  'application/rtf', // Rich text format
] as const;

const ALLOWED_CONTENT_TYPES_DESCRIPTIONS = [
  'Images (JPG, PNG, etc.)',
  'PDF documents',
  'Word documents (.doc, .docx)',
  'Excel spreadsheets (.xls, .xlsx)',
  'CSV files',
  'Rich text documents (.rtf)',
] as const;

const MAX_FILENAME_LENGTH = 1024;

const sanitizeFileName = (fileName: string): string => {
  // Only alphanumeric, !, -, _, ., *, ', (, and ) are allowed
  // Remove all characters that are NOT allowed
  const sanitized = fileName.replace(/[^a-zA-Z0-9!\-_.*'()]/g, '');

  // If sanitization results in empty string, provide a default
  if (!sanitized) {
    return 'file';
  }

  // Truncate if too long
  if (sanitized.length > MAX_FILENAME_LENGTH) {
    return sanitized.substring(0, MAX_FILENAME_LENGTH);
  }

  return sanitized;
};

const isValidContentType = (contentType: string): boolean => {
  return ALLOWED_CONTENT_TYPES.some((allowedType) => {
    if (allowedType.endsWith('*')) {
      const baseType = allowedType.slice(0, -1);
      return contentType.startsWith(baseType);
    }
    return contentType === allowedType;
  });
};

interface PresignedUrlResponse {
  success: boolean;
  data: {
    uploadUrl: string;
    fileUrl: string;
    fileKey: string;
  };
}

export interface GetPresignedUrlProps {
  urlSlug: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  publicId?: string;
  isSigner?: boolean;
  trackingId?: string;
}

export const getPresignedUrl = async ({
  urlSlug,
  fileName,
  contentType,
  fileSize,
  publicId,
  isSigner,
  trackingId,
}: GetPresignedUrlProps): Promise<PresignedUrlResponse> => {
  // Sanitize filename instead of validating
  const sanitizedFileName = sanitizeFileName(fileName);

  if (!isValidContentType(contentType)) {
    throw new Error(`Invalid file type. Allowed types are: ${ALLOWED_CONTENT_TYPES_DESCRIPTIONS.join(', ')}`);
  }

  let url: string;
  let body: {
    fileName: string;
    contentType: string;
    fileSize: number;
    isSigner?: boolean;
  };

  if (trackingId) {
    // Third parties flow
    if (!trackingId || !urlSlug) {
      throw new Error('Cannot upload files before third party is created');
    }
    url = `${process.env.REACT_APP_API_URL}/third-parties/${urlSlug}/${trackingId}/files/presigned-url`;
    body = {
      fileName: sanitizedFileName,
      contentType,
      fileSize,
    };
  } else if (publicId) {
    // Submission flow
    if (!publicId || !urlSlug) {
      throw new Error('Cannot upload files before submission is created');
    }
    url = `${process.env.REACT_APP_API_URL}/submissions/${urlSlug}/${publicId}/files/presigned-url`;
    body = {
      fileName: sanitizedFileName,
      contentType,
      fileSize,
      isSigner: isSigner || false,
    };
  } else {
    throw new Error('Either trackingId or publicId must be provided');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get presigned URL: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};
